import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query';

import Logger from '../util/logger';
import { getAccessTokenWithAudience } from '../util/auth0Utils';

// Auth0 functions will be injected via the context
let getAccessTokenSilently: ((options?: any) => Promise<string>) | null = null;

export const setAuth0Functions = (getTokenFn: (options?: any) => Promise<string>) => {
  getAccessTokenSilently = getTokenFn;
};

export const prepareHeaders = async (headers: Headers): Promise<Headers> => {
  try {
    if (!getAccessTokenSilently) {
      throw new Error('Auth0 getAccessTokenSilently function not available');
    }

    // Always get fresh token from Auth0 - it handles caching internally
    const token = await getAccessTokenWithAudience(getAccessTokenSilently);

    if (!token) {
      throw new Error('No authorization token found');
    }

    headers.set('authorization', `Bearer ${token}`);
    return headers;
  } catch (error: any) {
    Logger.error('Error in prepareHeaders:', error);
    throw error;
  }
};

export const fetchBaseQueryWithAuth =
  (
    baseUrl: string,
  ): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, { noAuth?: boolean }> =>
  async (args, api, extraOptions) => {
    const baseQuery = fetchBaseQuery({
      baseUrl,
      prepareHeaders: extraOptions?.noAuth ? (headers) => headers : prepareHeaders,
    });

    const result = await baseQuery(args, api, extraOptions);

    if (result.error?.status === 401 || result.error?.status === 403) {
      Logger.warn('Authentication error in API call');
    } else if (
      result.error?.status === 500 ||
      result.error?.status === 503 ||
      result.error?.status === 502
    ) {
      Logger.warn(`Service unavailable: ${result.error.status} - ${baseUrl}`);
    }

    return result;
  };
